import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import adminApiClient, { FeatureBetaGroupInput } from "../../../api/admin";

export const FEATURE_BETA_GROUPS_QUERY_KEY = ["feature-beta-groups"];

export const useFeatureBetaGroups = () => {
  return useQuery({
    queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
    queryFn: () => adminApiClient.getFeatureBetaGroups(),
  });
};

export const useFeatureBetaGroupClocks = (
  id: string | null,
  params?: { skip?: number; limit?: number; search?: string },
) => {
  return useQuery({
    queryKey: [...FEATURE_BETA_GROUPS_QUERY_KEY, id, "clocks", params],
    queryFn: () =>
      adminApiClient.getFeatureBetaGroupClocks(id as string, params),
    enabled: !!id,
    // Keeps the rows on screen while paging or searching within a group, but
    // not across groups: another group's clocks would show until the fetch
    // lands.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === id ? previous : undefined,
  });
};

export const useCreateFeatureBetaGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FeatureBetaGroupInput) =>
      adminApiClient.createFeatureBetaGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
      });
    },
  });
};

export const useUpdateFeatureBetaGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<FeatureBetaGroupInput>;
    }) => adminApiClient.updateFeatureBetaGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
      });
    },
  });
};

export const useDeleteFeatureBetaGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.deleteFeatureBetaGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
      });
    },
  });
};

export const useAddClocksToFeatureBetaGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clockIds }: { id: string; clockIds: string[] }) =>
      adminApiClient.addClocksToFeatureBetaGroup(id, clockIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
      });
    },
  });
};

export const useRemoveClocksFromFeatureBetaGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clockIds }: { id: string; clockIds: string[] }) =>
      adminApiClient.removeClocksFromFeatureBetaGroup(id, clockIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
      });
    },
  });
};

export const useSendFeatureBetaGroupUpdate = () => {
  return useMutation({
    mutationFn: (id: string) => adminApiClient.sendFeatureBetaGroupUpdate(id),
  });
};
